import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/posthog';
import { useTranslation } from 'react-i18next';

interface ManageSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
}

export function ManageSubscriptionModal({ open, onClose, subscriptionId }: ManageSubscriptionModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');

  const handleCancel = async () => {
    setError('');
    setLoading(true);
    try {
      const { error: err } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'cancel', subscriptionId },
      });
      if (err) throw err;
      trackEvent('subscription_cancelled');
      setCancelled(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmCancel(false);
    setCancelled(false);
    setError('');
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('manageSubscription.title')}>
      {cancelled ? (
        <div className="text-center py-4">
          <p className="text-sm font-semibold text-emerald-700">{t('manageSubscription.cancelled')}</p>
          <button
            onClick={handleClose}
            className="mt-6 w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors"
          >
            OK
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {!confirmCancel ? (
            <button
              onClick={() => setConfirmCancel(true)}
              className="w-full bg-rose-50 text-rose-600 py-4 rounded-2xl font-bold text-sm hover:bg-rose-100 transition-colors"
            >
              {t('manageSubscription.cancel')}
            </button>
          ) : (
            <button
              onClick={handleCancel}
              disabled={loading}
              className="w-full bg-rose-500 text-white py-4 rounded-2xl font-bold text-sm hover:bg-rose-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('manageSubscription.confirmCancel')}
            </button>
          )}

          {error && <p className="text-sm font-semibold text-rose-500 text-center">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
